/**
 * «Результаты»: все генерации хронологически — дни, цепочки (генерация → доработки → улучшения,
 * по последнему шагу, `lib/resultChains`), плитки по изображениям. Фильтры серверные и живут в адресе
 * (`?kind=&status=&pick=enhance|publish&posted=&network=&author=`); страницы — «Показать
 * ещё» по курсору `next_before`; пока что-то в очереди или в работе, лента
 * опрашивает сервис сама. «Метка: В публикацию» — не цепочки, а сетка только отмеченных изображений
 * (`publishTiles`). Галочки на плитках (по `variant_id`, до 4) копят выбор; полоса снизу открывает
 * «Сравнить» в новой вкладке (`lib/comparePick`).
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowRight, Upload } from "lucide-react";
import { Fragment, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";

import { parseResultFilter } from "../features/results/model/resultsFilter";
import { UploadDialog } from "@/components/results/UploadDialog";
import { PostEntry } from "@/components/results/PostPanel";
import { ResultTile } from "@/components/results/ResultTile";
import { btn, Empty, ErrorLine, input, Loading, Segmented } from "@/components/trends/ui";
import { isRouterModel } from "@/lib/fitting";
import { COMPARE_MAX, COMPARE_MIN, compareHref, toggleSelect } from "@/lib/comparePick";
import { plural } from "@/lib/library";
import { loadCandidates, postsKeys } from "@/lib/postsApi";
import { usd } from "@/lib/project";
import { chainGroups, stepStarts, type Chain } from "@/lib/resultChains";
import { enhanceNote, publishTiles, STAGE_LABEL } from "@/lib/verdict";
import {
  loadResults,
  pickResult,
  resultKeys,
  RESULTS_PREFIX,
  type Owner,
  type ResultFilter,
  type ResultGen,
} from "@/lib/results";

const KIND_LABEL: Record<string, string> = {
  shot: "Кадр",
  fitting: "Примерка",
  reference: "Референс",
  manual: "Вручную",
  clip: "Видео-клип",
  upload: "Загружено",
  enhance: "Улучшено",
  other: "Другое",
};

function dayLabel(day: string): string {
  const d = new Date(day + "T00:00:00");
  return Number.isNaN(d.getTime()) ? day : d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

export function ResultsPage() {
  const [search, setSearch] = useSearchParams();
  const filter: ResultFilter = parseResultFilter(search);
  const filtered = Object.keys(filter).length > 0;

  const patch = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(search);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearch(next, { replace: true });
  };

  const client = useQueryClient();
  const [uploading, setUploading] = useState(false);
  // выбор для сравнения живёт здесь, по variant_id: переживает «Показать ещё», опрос и смену фильтров
  const [selected, setSelected] = useState<number[]>([]);
  const select = {
    onSelect: (id: number) => setSelected((s) => toggleSelect(s, id)),
    selectDisabled: selected.length >= COMPARE_MAX,
  };
  const feed = useInfiniteQuery({
    queryKey: resultKeys.feed(filter),
    queryFn: ({ pageParam }) => loadResults(filter, pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (p) => p.next_before ?? undefined,
    staleTime: 5_000,
    placeholderData: keepPreviousData,
    refetchInterval: (q) => ((q.state.data?.pages.some((p) => p.pending > 0) ?? false) ? 3_000 : false),
  });

  // Лайк/дизлайк ПЕРЕКЛЮЧАЮТ: замок в ref — второй клик приходит раньше перерисовки.
  const picking = useRef(false);
  // Итог автозапуска улучшения после последней отметки.
  const [enhanceText, setEnhanceText] = useState<string | null>(null);
  const star = useMutation({
    mutationFn: ({ id, status }: { id: number; status: "picked" | "rejected" }) => pickResult(id, status),
    onSettled: () => {
      picking.current = false;
    },
    onSuccess: (got) => {
      setEnhanceText(enhanceNote(got.enhance));
      return client.invalidateQueries({ queryKey: RESULTS_PREFIX });
    },
  });
  const onPick = (id: number, status: "picked" | "rejected") => {
    if (picking.current) return;
    picking.current = true;
    star.mutate({ id, status });
  };

  const pages = feed.data?.pages ?? [];
  const first = pages[0];
  const gens = pages.flatMap((p) => p.gens);
  const pending = pages.reduce((sum, p) => sum + p.pending, 0);
  const groups = chainGroups(gens);
  // «В публикацию» — плоская сетка только отмеченных изображений, без цепочек и дней
  const publishView = filter.pick === "publish";
  const tiles = publishView ? publishTiles(gens) : [];

  // публикации плиток — одной выборкой на всю сетку, сопоставление по variant_id
  const cands = useQuery({
    queryKey: postsKeys.candidates(),
    queryFn: () => loadCandidates(),
    enabled: publishView && tiles.length > 0,
    retry: 1,
  });
  // вне выборки (она режется на 200) — undefined: плитка сама спросит candidates(variant_id)
  const plansOf = (id: number) => cands.data?.find((c) => c.variant_id === id)?.plans;

  let lastDay = "";
  const body: ReactNode[] = [];
  for (const chain of groups) {
    const day = chain.day;
    if (day !== lastDay) {
      lastDay = day;
      body.push(
        <h2
          key={`day-${day}`}
          className="mt-2 border-b border-border pb-1 text-[13px] font-semibold text-fg"
          data-annot="день"
        >
          {dayLabel(day)}
        </h2>,
      );
    }
    body.push(
      <Group
        key={chain.id}
        chain={chain}
        onPick={onPick}
        picking={star.isPending ? (star.variables?.id ?? null) : null}
        selected={selected}
        select={select}
      />,
    );
  }

  return (
    <div className="grid gap-3 p-5" data-annot="лента результатов">
      <div className="flex items-start gap-3">
        <div>
          <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">Результаты</h1>
          <p className="mt-0.5 text-[12.5px] text-fg-muted">
            новые сверху{pending > 0 ? ` · в очереди и в работе: ${pending}` : ""}
          </p>
        </div>
        <button type="button" className={`${btn} ml-auto inline-flex items-center gap-1.5`} onClick={() => setUploading(true)}>
          <Upload size={14} />
          Загрузить
        </button>
      </div>
      <UploadDialog open={uploading} onClose={() => setUploading(false)} />
      {enhanceText ? <div className="text-[12px] text-fg-muted">{enhanceText}</div> : null}

      <div className="flex flex-wrap items-center gap-2" data-annot="фильтры результатов">
        <Select
          title="Вид"
          value={filter.kind ?? ""}
          onChange={(v) => patch({ kind: v })}
          options={[
            ["", "Вид: все"],
            ["shot", "Кадр"],
            ["fitting", "Примерка"],
            ["reference", "Референс"],
            ["manual", "Вручную"],
            ["clip", "Видео-клип"],
            ["upload", "Загружено"],
            ["enhance", "Улучшено"],
          ]}
        />
        <Select
          title="Статус"
          value={filter.status ?? ""}
          onChange={(v) => patch({ status: v })}
          options={[
            ["", "Статус: все"],
            ["done", "Готово"],
            ["active", "В работе"],
            ["failed", "Ошибка"],
          ]}
        />
        <Segmented<"" | "enhance" | "publish">
          label="Метка"
          options={[
            { value: "", label: "все" },
            { value: "enhance", label: STAGE_LABEL.enhance },
            { value: "publish", label: STAGE_LABEL.publish, title: "только отмеченные «В публикацию», плиткой" },
          ]}
          value={filter.pick ?? ""}
          onPick={(v) => patch({ pick: v || null, picked: null })}
        />
        <button
          type="button"
          aria-pressed={filter.kind === "upload"}
          title="То же, что «Вид: Загружено»"
          className={clsx(btn, filter.kind === "upload" && "border-accent bg-accent/10 font-medium text-accent-text")}
          onClick={() => patch({ kind: filter.kind === "upload" ? null : "upload" })}
        >
          Загруженные
        </button>
        <Select
          title="Публикации"
          value={filter.posted ?? ""}
          onChange={(v) => patch({ posted: v })}
          options={[
            ["", "Публикации: все"],
            ["yes", "Опубликованные"],
            ["no", "Не опубликованные"],
          ]}
        />
        <Select
          title="Сеть"
          value={filter.network ?? ""}
          onChange={(v) => patch({ network: v })}
          options={[["", "Сеть: все"], ...(first?.networks ?? []).map((n): [string, string] => [n.code, n.name])]}
        />
        <Select
          title="Автор"
          value={filter.author !== undefined ? String(filter.author) : ""}
          onChange={(v) => patch({ author: v })}
          options={[
            ["", "Автор: все"],
            ...(first?.authors ?? []).map((a): [string, string] => [String(a.user_id), a.name]),
          ]}
        />
      </div>

      {feed.isError ? <ErrorLine error={feed.error} /> : null}
      <ErrorLine error={star.error} />
      {publishView && cands.isError ? <ErrorLine error={cands.error} /> : null}
      {!first && !feed.isError ? (
        <Loading />
      ) : first && (publishView ? tiles.length === 0 : groups.length === 0) ? (
        <Empty>{filtered ? "Под фильтр ничего не попало" : "Генераций нет"}</Empty>
      ) : publishView ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2" data-annot="в публикацию">
          {tiles.map((g) => (
            <div key={g.variant_id} className="grid content-start gap-1">
              <ResultTile
                g={g}
                fill
                selected={g.variant_id !== null && selected.includes(g.variant_id)}
                {...select}
                onPick={onPick}
                picking={star.isPending && star.variables?.id === g.variant_id}
              />
              {g.kind === "clip" || g.video_ms != null || cands.isPending ? null : (
                <PostEntry variantId={g.variant_id!} plans={plansOf(g.variant_id!)} />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-3">{body}</div>
      )}
      {feed.hasNextPage ? (
        <div>
          <button type="button" className={btn} disabled={feed.isFetchingNextPage} onClick={() => feed.fetchNextPage()}>
            {feed.isFetchingNextPage ? "Загружаю…" : "Показать ещё"}
          </button>
        </div>
      ) : null}
      {selected.length > 0 ? (
        <div className="sticky bottom-0 -mx-5 flex items-center gap-3 border-t border-border bg-bg px-5 py-2">
          <span className="text-[12.5px] text-fg">Выбрано {selected.length}</span>
          {selected.length >= COMPARE_MIN ? (
            <a href={compareHref(selected)} target="_blank" rel="noopener" className={btn}>
              Сравнить
            </a>
          ) : (
            <button type="button" className={btn} disabled title="нужно 2–4">
              Сравнить
            </button>
          )}
          <button type="button" className={btn} onClick={() => setSelected([])}>
            Сбросить
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Select({
  title,
  value,
  onChange,
  options,
}: {
  title: string;
  value: string;
  onChange: (value: string) => void;
  options: [string, string][];
}) {
  return (
    <select aria-label={title} title={title} className={input} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map(([v, text]) => (
        <option key={v} value={v}>
          {text}
        </option>
      ))}
    </select>
  );
}

export function OwnerLinks({ kind, owner }: { kind: string; owner: Owner }) {
  const link = "text-accent-text hover:underline";
  const { project, shot, pin, dress, reference } = owner;
  return (
    <>
      {(kind === "shot" || kind === "clip") && project && shot ? (
        <Link to={`/project/${project.id}/shot/${shot.id}`} className={link}>
          {project.name} → {shot.title}
        </Link>
      ) : null}
      {pin ? (
        <Link to={`/pins/${pin.id}`} className={link}>
          {pin.title ?? `пин #${pin.id}`}
        </Link>
      ) : null}
      {reference ? (
        reference.deleted ? (
          <span>
            референс удалён (был: {reference.library_name ?? "библиотека"} #{reference.id})
          </span>
        ) : (
          <Link to={`/lib/ref/${reference.id}`} className={link}>
            референс «{reference.library_name ?? "библиотека"}» #{reference.id}
          </Link>
        )
      ) : null}
      {dress ? (
        dress.deleted ? (
          <span>{dress.name ?? `Платье #${dress.id}`} (папка удалена)</span>
        ) : (
          <Link to={`/photos/${dress.id}`} className={link}>
            {dress.name ?? `Платье #${dress.id}`}
          </Link>
        )
      ) : owner.dress_uploaded || owner.kind === "manual" ? (
        <span>платье из загрузки</span>
      ) : null}
      {owner.kind === "manual" ? <span>без сцены</span> : null}
    </>
  );
}

function Group({
  chain,
  onPick,
  picking,
  selected,
  select,
}: {
  selected: number[];
  select: { onSelect: (id: number) => void; selectDisabled: boolean };
  chain: Chain<ResultGen>;
  onPick: (variantId: number, status: "picked" | "rejected") => void;
  picking: number | null;
}) {
  const rows = chain.rows;
  const head = rows[0]!; // исходник цепочки: вид, владелец, автор
  const steps = new Set(rows.map((r) => r.generation_id)).size;
  const cost = `${chain.approx ? "~" : ""}${usd(chain.cost)}`;

  return (
    <div className="grid gap-1.5" data-annot="цепочка результатов">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-fg-muted">
        <span className="rounded-[3px] bg-bg-elevated px-1.5 text-[11px] font-medium text-fg">
          {KIND_LABEL[head.kind] ?? head.kind}
        </span>
        <OwnerLinks kind={head.kind} owner={head.owner} />
        <span className="tnum">
          {isRouterModel(head.generation_model) ? head.generation_model : (head.preset ?? head.quality)} · {cost}
          {steps > 1 ? ` за ${steps} ${plural(steps, "шаг", "шага", "шагов")}` : ""}
        </span>
        <span>{head.generation_author_name}</span>
        <span className="tnum" title="время последнего шага">{chain.time}</span>
      </div>
      <div className="flex items-start gap-2 overflow-x-auto pb-1">
        {rows.map((g, i) => (
          <Fragment key={`${g.generation_id}-${g.variant_id ?? "x"}`}>
            {stepStarts(rows, i) ? (
              <ArrowRight size={14} className="mt-12 flex-none text-fg-faint" aria-hidden />
            ) : null}
            <ResultTile
              g={g}
              onPick={onPick}
              picking={g.variant_id !== null && picking === g.variant_id}
              selected={g.variant_id !== null && selected.includes(g.variant_id)}
              {...select}
            />
          </Fragment>
        ))}
      </div>
    </div>
  );
}
